import { BrokerType, CanonicalLoad, ClientMyBroker } from '@numeo/types-core'
import { LoadFiltersState } from '../types/filters'
import { CompanyFilterOptions } from 'components/shared/LoadsTable/CompanyFilterButton'

const isMcFavorite = (brokers: ClientMyBroker[], mc: string) => brokers.some((b) => b.mcNumber === mc && b.type === BrokerType.FAVORITE)
const isMcBlocked = (brokers: ClientMyBroker[], mc: string) => brokers.some((b) => b.mcNumber === mc && b.type === BrokerType.IN_BLACKLIST)

/**
 * Apply the Company dropdown filter (Show Only / Exclude + company names, and the
 * Preferred / Blocked / Non-blocked list) to a set of loads. Mirrors the shared
 * NewLoadsTable logic so both surfaces behave identically. Blocked brokers are hidden
 * for every 'all' selection (matching "Show All Non-blocked Companies").
 */
export const applyCompanyFilter = (loads: CanonicalLoad[], options: CompanyFilterOptions | undefined, brokers: ClientMyBroker[]): CanonicalLoad[] => {
    if (!options) return loads

    const isDefaultAll = options.mode === 'show' && options.listType === 'all' && options.companyNames.length === 0
    if (isDefaultAll) {
        return loads.filter((load) => !isMcBlocked(brokers, load.brokerMc || ''))
    }

    return loads.filter((load) => {
        const companyName = load.brokerCompanyName || ''
        const mc = load.brokerMc || ''
        const isBlocked = isMcBlocked(brokers, mc)

        // 'all' list always drops blocked brokers regardless of the name filter.
        if (options.listType === 'all' && isBlocked) return false

        // Company-name filter only applies in 'all' mode.
        if (options.listType === 'all' && options.companyNames.length > 0) {
            const nameMatches = options.companyNames.includes(companyName)
            if (options.mode === 'show' && !nameMatches) return false
            if (options.mode === 'exclude' && nameMatches) return false
        }

        // Preferred / Blocked list filtering.
        if (options.listType !== 'all') {
            const isPreferred = isMcFavorite(brokers, mc)
            if (options.listType === 'preferred') {
                if (options.mode === 'show' && !isPreferred) return false
                if (options.mode === 'exclude' && isPreferred) return false
            } else if (options.listType === 'blocked') {
                if (options.mode === 'show' && !isBlocked) return false
                if (options.mode === 'exclude' && isBlocked) return false
            }
        }

        return true
    })
}

/** Canonical filter function — applies all client-side filters to loads */
export const filterLoads = (loads: CanonicalLoad[], filters: LoadFiltersState): CanonicalLoad[] => {
    let result = loads

    // Filter by isActive (Show all loads)
    if (filters.showAllLoads === 'active') {
        result = result.filter((load) => load.isActive === true)
    } else if (filters.showAllLoads === 'inactive') {
        result = result.filter((load) => load.isActive === false)
    }

    // Filter by source type (Load sources) - multi-select
    if (filters.loadSources.length > 0) {
        result = result.filter((load) => filters.loadSources.includes(load.sourceType))
    }

    // Filter by companies
    if (filters.companies.length > 0) {
        result = result.filter((load) => load.brokerCompanyName && filters.companies.includes(load.brokerCompanyName))
    }

    // Filter by max distance miles
    if (filters.maxMi) {
        const maxMi = parseFloat(filters.maxMi)
        if (!isNaN(maxMi)) {
            result = result.filter((load) => load.distanceMiles && load.distanceMiles <= maxMi)
        }
    }

    // Filter by min distance miles
    if (filters.minMi) {
        const minMi = parseFloat(filters.minMi)
        if (!isNaN(minMi)) {
            result = result.filter((load) => load.distanceMiles && load.distanceMiles >= minMi)
        }
    }

    // Filter by min weight (lbs) — loads without a known weight are excluded (can't confirm the minimum).
    if (filters.minWeight) {
        const minWeight = parseFloat(filters.minWeight)
        if (!isNaN(minWeight)) {
            result = result.filter((load) => load.weightLbs != null && load.weightLbs >= minWeight)
        }
    }

    // Filter by min rate
    if (filters.minR) {
        const minR = parseFloat(filters.minR)
        if (!isNaN(minR)) {
            result = result.filter((load) => load.rateAmountUsd && load.rateAmountUsd >= minR)
        }
    }

    // Filter by RPM ($/mi)
    if (filters.minT) {
        const minRpm = parseFloat(filters.minT)
        if (!isNaN(minRpm) && minRpm > 0) {
            result = result.filter((load) => {
                if (!load.rateAmountUsd || !load.distanceMiles || load.distanceMiles === 0) return false
                return load.rateAmountUsd / load.distanceMiles >= minRpm
            })
        }
    }

    // Filter by search back (load age)
    if (filters.searchBack) {
        const match = filters.searchBack.match(/^(\d+)h$/)
        if (match) {
            const hours = parseInt(match[1], 10)
            const cutoff = Date.now() - hours * 60 * 60 * 1000
            result = result.filter((load) => {
                if (!load.createdAt) return true
                const createdTime = new Date(load.createdAt).getTime()
                return createdTime >= cutoff
            })
        }
    }

    return result
}

/** Deduplicate loads with identical route, rate, miles, weight and equipment. Keeps the first occurrence. */
export const deduplicateLoads = (loads: CanonicalLoad[]): CanonicalLoad[] => {
    const seen = new Map<string, CanonicalLoad>()
    const duplicates: Array<{ kept: CanonicalLoad; removed: CanonicalLoad }> = []

    const result = loads.filter((load) => {
        const key = [
            (load.pickupCity ?? '').toLowerCase(),
            (load.pickupStateProv ?? '').toLowerCase(),
            (load.deliveryCity ?? '').toLowerCase(),
            (load.deliveryStateProv ?? '').toLowerCase(),
            load.rateAmountUsd ?? '',
            load.distanceMiles ?? '',
            load.equipmentType ?? '',
        ].join('|')
        const existing = seen.get(key)
        if (existing) {
            duplicates.push({ kept: existing, removed: load })
            return false
        }
        seen.set(key, load)
        return true
    })

    return result
}

export const sortLoadsByRate = (loads: CanonicalLoad[], desc = true): CanonicalLoad[] => {
    return [...loads].sort((a, b) => {
        const rateA = a.rateAmountUsd ?? 0
        const rateB = b.rateAmountUsd ?? 0
        return desc ? rateB - rateA : rateA - rateB
    })
}

export const sortLoadsByDistance = (loads: CanonicalLoad[], desc = false): CanonicalLoad[] => {
    return [...loads].sort((a, b) => {
        const distA = a.distanceMiles ?? 0
        const distB = b.distanceMiles ?? 0
        return desc ? distB - distA : distA - distB
    })
}
